import { ChangeDetectorRef, Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { ChatbotService, RangeType, TICKERS, TIME_OPTIONS } from '../../services/chatbot.service';
import { AuthService } from '../../services/auth.service';

interface EconTableSection { name: string; rows: string[][] }
interface EconTableData { title: string; headers: string[]; sections: EconTableSection[] }

interface ChatMessage {
  from: 'bot' | 'user';
  text: string;
  note?: boolean;
  intro?: boolean;
  table?: EconTableData;
}

type Step = 'ticker' | 'time' | 'econ' | 'holiday' | 'holiday-period';
type HolidayPeriod = 'week-before' | 'day-before' | 'day-after';

@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MatIconModule],
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
})
export class ChatbotComponent implements OnInit, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  isOpen = false;
  isMaximized = false;
  isLoading = false;
  messages: ChatMessage[] = [];

  readonly tickers = TICKERS;
  readonly timeOptions = TIME_OPTIONS;

  step: Step = 'ticker';
  selectedTicker = '';

  // Economic events step
  econEventNames: string[] = [];
  selectedEconEvents = new Set<string>();
  econLoading = false;

  // Holiday step
  holidays: { date: string; name: string }[] = [];
  uniqueHolidayNames: string[] = [];
  selectedHolidayNames = new Set<string>();
  selectedHolidayPeriods = new Set<HolidayPeriod>();
  holidayLoading = false;

  readonly holidayPeriodOptions: { id: HolidayPeriod; label: string }[] = [
    { id: 'week-before', label: 'Week Before' },
    { id: 'day-before',  label: 'Day Before'  },
    { id: 'day-after',   label: 'Day After'   },
  ];

  constructor(private chatbot: ChatbotService, public auth: AuthService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.messages = [{
      from: 'bot',
      text: "Pick a ticker below to query range forecasts, next day bias, economic event impact, or holiday market movement.",
      intro: true,
    }];
  }

  ngAfterViewChecked(): void {
    const count = this.messages.length + (this.isLoading ? 1 : 0);
    if (count !== this._lastScrollCount) {
      this._lastScrollCount = count;
      this.scrollToBottom();
    }
  }

  private _lastScrollCount = 0;

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) this.isMaximized = false;
  }

  toggleMaximize(): void {
    this.isMaximized = !this.isMaximized;
  }

  selectTicker(ticker: string): void {
    this.selectedTicker = ticker;
    this.step = 'time';
    this.messages.push({ from: 'user', text: ticker });
    this.messages.push({ from: 'bot', text: `${ticker} — what would you like to know?` });
  }

  onTimeSelect(rangeType: RangeType): void {
    const labels: Record<RangeType, string> = {
      today: "Today's Range",
      yesterday: "Yesterday's Range",
      bias: 'Next Day Bias',
      econ: 'Economic Events',
      holiday: 'Holiday Movement',
    };
    this.messages.push({ from: 'user', text: labels[rangeType] });

    if (rangeType === 'holiday') {
      this.step = 'holiday';
      this.holidays = [];
      this.uniqueHolidayNames = [];
      this.selectedHolidayNames.clear();
      this.selectedHolidayPeriods.clear();
      this.holidayLoading = true;
      this.cdr.detectChanges();
      this.chatbot.loadHolidays().then(list => {
        this.holidays = list;
        const seen = new Set<string>();
        this.uniqueHolidayNames = list.map(h => h.name).filter(n => { if (seen.has(n)) return false; seen.add(n); return true; });
        this.holidayLoading = false;
        this.cdr.detectChanges();
      }).catch(() => {
        this.holidayLoading = false;
        this.messages.push({ from: 'bot', text: 'Could not load holidays. Please try again.' });
        this.step = 'time';
        this.cdr.detectChanges();
      });
      return;
    }

    if (rangeType === 'econ') {
      this.step = 'econ';
      this.selectedEconEvents.clear();
      this.econEventNames = [];
      this.econLoading = true;
      this.cdr.detectChanges();
      this.chatbot.loadEconEventNames().then(names => {
        this.econEventNames = names;
        this.econLoading = false;
        this.cdr.detectChanges();
      }).catch(() => {
        this.econEventNames = [];
        this.econLoading = false;
        this.messages.push({ from: 'bot', text: 'Could not load economic events. Please try again.' });
        this.step = 'time';
        this.cdr.detectChanges();
      });
      return;
    }

    this.fetchRange(rangeType);
  }

  toggleEconEvent(name: string): void {
    if (this.selectedEconEvents.has(name)) {
      this.selectedEconEvents.delete(name);
    } else {
      this.selectedEconEvents.add(name);
    }
  }

  submitEconEvents(): void {
    const selected = [...this.selectedEconEvents];
    if (!selected.length) return;
    this.messages.push({ from: 'user', text: selected.join(', ') });
    this.fetchEconEvents(selected);
  }

  toggleHolidayName(name: string): void {
    if (this.selectedHolidayNames.has(name)) this.selectedHolidayNames.delete(name);
    else this.selectedHolidayNames.add(name);
  }

  toggleAllHolidays(): void {
    if (this.selectedHolidayNames.size === this.uniqueHolidayNames.length) {
      this.selectedHolidayNames.clear();
    } else {
      this.uniqueHolidayNames.forEach(n => this.selectedHolidayNames.add(n));
    }
  }

  submitHolidayNames(): void {
    if (!this.selectedHolidayNames.size) return;
    this.messages.push({ from: 'user', text: [...this.selectedHolidayNames].join(', ') });
    this.step = 'holiday-period';
    this.selectedHolidayPeriods.clear();
  }

  toggleHolidayPeriod(p: HolidayPeriod): void {
    if (this.selectedHolidayPeriods.has(p)) this.selectedHolidayPeriods.delete(p);
    else this.selectedHolidayPeriods.add(p);
  }

  submitHolidayPeriods(): void {
    if (!this.selectedHolidayPeriods.size) return;
    const periodLabels: Record<HolidayPeriod, string> = {
      'week-before': 'Week Before', 'day-before': 'Day Before', 'day-after': 'Day After',
    };
    this.messages.push({ from: 'user', text: [...this.selectedHolidayPeriods].map(p => periodLabels[p]).join(', ') });
    this.fetchHoliday([...this.selectedHolidayNames], [...this.selectedHolidayPeriods]);
  }

  backToTickers(): void {
    this.step = 'ticker';
    this.selectedTicker = '';
    this.selectedEconEvents.clear();
  }

  backToTime(): void {
    this.step = 'time';
    this.selectedEconEvents.clear();
  }

  private async fetchRange(rangeType: RangeType, date?: string): Promise<void> {
    const ticker = this.selectedTicker;
    this.isLoading = true;
    try {
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 30_000)
      );
      const answer = await Promise.race([
        this.chatbot.answerRange(ticker, rangeType, date),
        timeout,
      ]);
      this.pushAnswer(answer);
      this.messages.push({ from: 'bot', text: `Anything else you'd like to know about ${ticker}?` });
      this.step = 'time';
    } catch (err: any) {
      const msg = err?.message === 'timeout'
        ? 'Request timed out. The data may not be available yet — please try again.'
        : 'Unable to fetch data. Please try again.';
      this.messages.push({ from: 'bot', text: msg });
      this.step = 'time';
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private async fetchHoliday(names: string[], periods: HolidayPeriod[]): Promise<void> {
    const ticker = this.selectedTicker;
    this.selectedHolidayNames.clear();
    this.selectedHolidayPeriods.clear();
    this.isLoading = true;
    try {
      const answer = await this.chatbot.answerHoliday(ticker, this.holidays, names, periods);
      this.pushAnswer(answer);
      this.messages.push({ from: 'bot', text: `Anything else you'd like to know about ${ticker}?` });
      this.step = 'time';
    } catch {
      this.messages.push({ from: 'bot', text: 'Unable to fetch holiday data. Please try again.' });
      this.step = 'time';
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private async fetchEconEvents(eventNames: string[]): Promise<void> {
    const ticker = this.selectedTicker;
    this.selectedEconEvents.clear();
    this.isLoading = true;
    try {
      const answer = await this.chatbot.answerEconEvents(ticker, eventNames);
      this.pushAnswer(answer);
      this.messages.push({ from: 'bot', text: `Anything else you'd like to know about ${ticker}?` });
      this.step = 'time';
    } catch {
      this.messages.push({ from: 'bot', text: 'Unable to fetch event data. Please try again.' });
      this.step = 'time';
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private pushAnswer(answer: string): void {
    if (answer.startsWith('TABLE:')) {
      try {
        const table: EconTableData = JSON.parse(answer.slice(6));
        this.messages.push({ from: 'bot', text: table.title, table });
      } catch {
        this.messages.push({ from: 'bot', text: answer });
      }
      return;
    }
    const NOTE_PREFIX = 'ℹ  ';
    if (answer.includes('\n' + NOTE_PREFIX)) {
      const idx = answer.lastIndexOf('\n' + NOTE_PREFIX);
      this.messages.push({ from: 'bot', text: answer.slice(0, idx).trimEnd() });
      this.messages.push({ from: 'bot', text: answer.slice(idx + 1), note: true });
    } else {
      this.messages.push({ from: 'bot', text: answer });
    }
  }

  private scrollToBottom(): void {
    try {
      const el = this.messagesContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch { /* ignore */ }
  }
}
